import { useEffect } from 'react';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'src/config/request';
import { useIsExtraModule } from 'src/modules/contentList/hooks/useIsExtraModule';
import { contentListQueries } from 'src/modules/contentList/queries';
import { ModuleActionsService } from 'src/modules/contentList/services/moduleActionsService';
import { Toast } from 'src/shared/components/toast';
export const MODULE_DRAG_HANDLE_PROPS = { 'data-draghandle': 'module' };
export const MODULE_DRAG_NODE_PROPS = { 'data-dragnode': 'module' };
export function useModuleDragAndDrop({ modules, setDraggingModuleId }) {
    const { t } = useTranslation();
    const isExtraModule = useIsExtraModule();
    const reorderModuleMutation = useMutation({
        mutationFn: ModuleActionsService.reorderModule,
        mutationKey: ['reorderModule']
    });
    const [parentRef, modulesCopy, setModulesCopy] = useDragAndDrop(modules ?? [], {
        group: 'modules',
        dragHandle: '[data-draghandle="module"]',
        draggable: (child) => child.dataset.dragnode === 'module',
        onDragstart: function (data) {
            setDraggingModuleId(data.draggedNode.data.value.id);
        },
        onDragend: async function (data) {
            setDraggingModuleId(null);
            try {
                // Get dragged module id
                const moduleNewIndex = data.state.targetIndex;
                const moduleId = data.draggedNodes[0].data.value.id;
                // Get target module id
                const targetModule = queryClient.getQueryData(contentListQueries.getModules({ extra: isExtraModule }).queryKey)?.[moduleNewIndex];
                const targetModuleId = targetModule?.id ?? null;
                if (moduleId === targetModuleId)
                    return;
                await reorderModuleMutation.mutateAsync({
                    moduleId,
                    targetModuleOrderId: targetModuleId
                });
                queryClient.invalidateQueries(contentListQueries.getModules({ extra: isExtraModule }));
                Toast.show({ type: 'success', message: t('content_list.reorder_module.success') });
            }
            catch {
                Toast.show({ type: 'danger', message: t('content_list.reorder_module.error') });
                setModulesCopy(modules ?? []);
            }
        }
    });
    useEffect(function updateDragAndDropModulesReference() {
        setModulesCopy(modules ?? []);
    }, [modules, setModulesCopy]);
    return {
        modulesCopy,
        parentRef
    };
}
